import { generateUUID } from "./uuid";

export type TaskState = string;

/** Document-level format version for drawing task collaboration data. */
export const DRAWING_TASK_DOC_FORMAT_VERSION = 2;

// ── Comment types ──

/** Schema version for DrawingComment data structure */
export const COMMENT_SCHEMA_VERSION = 2;

export type CommentState = "new" | "ongoing" | "fixed" | "approved";

export type CommentType = "shape" | "copy" | "note";

export interface TaskStateConfig {
	value: string;
	icon: string;
	position: number;
	translations: Record<string, { label: string; description: string }>;
	color: string;
	variant?: "default" | "secondary" | "destructive" | "outline";
	isSeparated?: boolean;
	requiresComment?: boolean;
}

export interface TaskBounds {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

export interface TaskCentroid {
	x: number;
	y: number;
	z: number;
}

export type IPoint = [number, number, number];

export type TaskSelectionMode = "task" | "object" | "inside";

export type ShapeType =
	| "rectangle"
	| "ellipse"
	| "polygon"
	| "arrow"
	| "text"
	| "segmentedPath"
	| "freePath"
	| "annotation"
	| "prohibition";

export type BorderStyle = "solid" | "dashed" | "jagged" | "cloud";

export interface ShapeStyle {
	strokeColor?: string;
	fillColor?: string;
	strokeWidth?: number;
	fontSize?: number;
	fontFamily?: string;
	textColor?: string;
	isBold?: boolean;
	isItalic?: boolean;
	isUnderline?: boolean;
	isSuperscript?: boolean;
	isSubscript?: boolean;
	borderStyle?: BorderStyle;
	dashSize?: number; // Size of dashes for dashed border (default: 10)
	jaggedAmplitude?: number; // Amplitude of zigzag for jagged border (default: 0.6)
	cloudAmplitude?: number; // Amplitude multiplier for cloud scallops (default: 1.0)
}

export interface ShapeTextSpan {
	text: string;
	isBold?: boolean;
	isItalic?: boolean;
	isUnderline?: boolean;
	isSuperscript?: boolean;
	isSubscript?: boolean;
}

export interface ShapeRichText {
	spans: ShapeTextSpan[];
}

/**
 * Authoring anchor for a segmentedPath with curve support. The segment from
 * anchor i to anchor i+1 is a cubic bézier with controls hOut(i) / hIn(i+1);
 * a missing control degenerates toward its anchor (both missing = straight).
 */
export interface PathAnchor {
	/** Anchor point (on the path) */
	p: IPoint;
	/** Incoming bézier control (absolute world coords) */
	hIn?: IPoint;
	/** Outgoing bézier control (absolute world coords) */
	hOut?: IPoint;
	/** Smooth anchor: hIn/hOut are kept collinear during edits */
	smooth?: boolean;
}

export interface ShapeData {
	type: ShapeType;
	outline: IPoint[];
	text?: string;
	textPosition?: IPoint;
	style?: ShapeStyle;
	richText?: ShapeRichText;
	/** For annotation shapes: the point where the arrow tip points to */
	annotationTarget?: IPoint;
	/** For text shapes: the top-left anchor position (click location) */
	textAnchor?: IPoint;
	/**
	 * For text/annotation shapes: fixed text-box width in world units.
	 * Text word-wraps inside it; height follows the wrapped layout.
	 * Absent → legacy auto-width (box grows with the longest line).
	 */
	boxWidth?: number;
	/** Measured text width for accurate centering (used by text/annotation shapes) */
	measuredTextWidth?: number;
	/** Measured text height for accurate centering (used by text/annotation shapes) */
	measuredTextHeight?: number;
	/**
	 * For segmentedPath: authoring geometry with optional curved segments.
	 * `outline` is always the flattened polyline of these anchors; consumers
	 * unaware of curves keep reading `outline`. Editors must keep both in sync
	 * (see pathAnchorsMatchOutline guard in @planvera/drawing-viewer).
	 */
	pathAnchors?: PathAnchor[];
}

/** Metadata for partial selection within a task object */
export interface DrawingTaskObjectMetadata {
	/** Handle positions for partial path selection [start, end] as 0-1 percentages */
	h?: [number, number];
	/** Region box for partial group selection [majorStart, minorStart, majorEnd, minorEnd] as 0-1 normalized within group bounds */
	r?: [number, number, number, number];
	/** Region polygon for partial group selection - 4 world-space corner points for clipping */
	rp?: [[number, number], [number, number], [number, number], [number, number]];
	/** Source object hash for clipped objects */
	sourceObjectHash?: string | null;
	/**
	 * Selected segment indices for segment-based path selection.
	 * Enables non-contiguous partial selection (e.g., [0, 2, 4] selects segments 0, 2, and 4).
	 */
	s?: number[];
	/**
	 * Total segment count at time of selection.
	 * Used for validation when path might be re-segmented after drawing update.
	 */
	st?: number;
	/** Object kind discriminator, e.g. "copy" or "omitted". */
	kind?: string;
	/** OmittedShape only: per-shape overlay color; DEFAULT_OMITTED_COLOR when unset. */
	omittedColor?: string;
	[key: string]: unknown;
}

/**
 * Which FileVersion's page frame a piece of geometry is expressed in.
 * Stamped on creation and on migration exact-match; absent on legacy data
 * (Realignment then falls back to a creation-time heuristic, see
 * docs/plans/drawing-revision-realign.md).
 */
export interface GeometryFrame {
	fileVersionId: string;
}

export interface DrawingTaskObject {
	/**
	 * Linkage key for task-object selection.
	 *
	 * CAD-backed tasks use the current revision path ID only when migration
	 * found a unique exact geometry match. Detached migrated tasks use
	 * `m:<oldPathId>` so they cannot collide with current revision path IDs.
	 * Rendering must use geometryBlob when present, not this key.
	 */
	h: string;
	layerId: number | null;
	bounds: TaskBounds;
	centroid: TaskCentroid;
	outline?: IPoint[];
	shapeType?: ShapeType;
	shapeData?: ShapeData;
	metadata?: DrawingTaskObjectMetadata;
	/**
	 * Embedded geometry blob for direct rendering.
	 * When present, render from this blob instead of looking up drawing objects.
	 * Uses DecodedGeometry format from geometry-encoding.ts.
	 */
	geometryBlob?: Uint8Array;
	/** Page frame the coordinates above are expressed in (see GeometryFrame). */
	geometryFrame?: GeometryFrame;
}

// ── OmittedShape (comment shape kind "omitted") ──

/** `metadata.kind` value for omitted comment objects. */
export const OMITTED_OBJECT_KIND = "omitted";
/** Prefix of every omitted object's unique `h` — never a CAD path id. */
export const OMITTED_HASH_PREFIX = "omitted_";
/**
 * Default omitted ("not installed") overlay color — lighter blue, next to the
 * pure-blue added/changed default. Must not collide with the comment-state or
 * default workflow-state palettes (both use #3b82f6 blue).
 */
export const DEFAULT_OMITTED_COLOR = "#00d9ff";

export const isOmittedObject = (obj: DrawingTaskObject): boolean =>
	obj.metadata?.kind === OMITTED_OBJECT_KIND;

/** Unique per-object hash; the CAD linkage lives in `metadata.sourceObjectHash`. */
export const createOmittedObjectHash = (): string =>
	`${OMITTED_HASH_PREFIX}${generateUUID()}`;

export const getOmittedSourceObjectHash = (
	obj: DrawingTaskObject,
): string | null =>
	typeof obj.metadata?.sourceObjectHash === "string"
		? obj.metadata.sourceObjectHash
		: null;

export const getOmittedColor = (obj: DrawingTaskObject): string =>
	typeof obj.metadata?.omittedColor === "string"
		? obj.metadata.omittedColor
		: DEFAULT_OMITTED_COLOR;

// ── Pin comments (commentType "note", one pin object) ──

/**
 * Kinds of point-anchored pin comments, in pin-glyph atlas order — the index
 * here is the atlas cell the renderer samples. Append only.
 */
export const PIN_KINDS = [
	"note",
	"measurement",
	"sample",
	"defect",
	"variation",
] as const;
export type PinKind = (typeof PIN_KINDS)[number];

/**
 * Disc color per pin kind — the drawing-surface pin fill and the comment-list
 * label chip share it. The comment state shows on the disc border instead.
 */
export const PIN_KIND_COLORS: Record<PinKind, string> = {
	// Post-it yellow
	note: "#facc15",
	measurement: "#0066ff",
	sample: "#16a34a",
	defect: "#ef4444",
	variation: "#8b5cf6",
};

/**
 * Comment-list label chip color per pin kind. Chips render the color as
 * TEXT on a faint tint, so post-it yellow is darkened to stay readable on a
 * light background; every other kind matches its disc.
 */
export const PIN_KIND_LABEL_COLORS: Record<PinKind, string> = {
	...PIN_KIND_COLORS,
	note: "#a16207",
};

const PIN_KIND_SET: ReadonlySet<string> = new Set(PIN_KINDS);

export const isPinKind = (value: unknown): value is PinKind =>
	typeof value === "string" && PIN_KIND_SET.has(value);

/** Raw (pre-localization) label older measurement comments were seeded with. */
const LEGACY_MEASUREMENT_LABEL = "measurement";

/**
 * Resolve a pin's kind. The pin object's `metadata.kind` is authoritative;
 * two legacy shapes still map to the measurement kind: `metadata.isMeasurement`
 * and comments that only carry the raw "measurement" label (created before
 * labels were localized at creation). Everything else is a plain note.
 */
export const getPinKind = ({
	metadata,
	labels,
}: {
	metadata?: DrawingTaskObjectMetadata | null;
	labels?: readonly string[] | null;
}): PinKind => {
	if (isPinKind(metadata?.kind)) {
		return metadata.kind;
	}
	if (metadata?.isMeasurement === true) {
		return "measurement";
	}
	if (labels?.includes(LEGACY_MEASUREMENT_LABEL)) {
		return "measurement";
	}
	return "note";
};

export interface DrawingTaskNoteMedia {
	id: string;
	s3Key: string;
	filename: string;
	size: number;
	mimeType: string;
	uploadedAt: number;
	mediaType: "image" | "video";
}

// Backward compatibility alias
export type DrawingTaskNoteImage = DrawingTaskNoteMedia;

export interface DrawingTaskNote {
	id: string;
	userId: string;
	userName: string;
	content: string;
	timestamp: number;
	editedAt?: number | null;
	images?: DrawingTaskNoteMedia[];
	/** Indicates note is restricted to task-detail viewers */
	isPrivate?: boolean;
}

export interface DrawingTaskDocMeta {
	taskDataFormatVersion: number;
}

export interface TaskLink {
	id: string;
	type: "drawing" | "file" | "url";
	targetId: string; // Drawing ID, File Instance ID, or URL for external links
	/** Display label for the link. For drawings/files, defaults to the file name but can be customized. */
	label: string;
	/** Optional icon name for URL links (e.g., "globe", "link", "file-text") */
	icon?: string;
}

export interface TaskActivityLogEntry {
	f: string; // field name: "state" | "assignedTo" | "title" | "priority" | "org" | "team" | "labels" | "dueDate" | "links" | "merge" | "split" | "mergeConflict" (realign merge: o = JSON { field, value, from }, v = kept value)
	u: string; // userId
	un?: string; // userName (optional, for display when user not in project members)
	t: number; // timestamp
	o?: string; // oldValue (optional)
	v?: string; // newValue (optional)
}

export interface DrawingTask {
	id: string;
	state: TaskState;
	userId: string;
	page: number | null;
	objects: DrawingTaskObject[];
	timestamp: number;
	createdAt?: number;
	notes: DrawingTaskNote[];
	title?: string | null;
	assignedTo?: string | null;
	responsibleOrganization?: string | null;
	/** Project Team answerable for this item; its members are Followers (ADR 0036). */
	responsibleTeamId?: string | null;
	priority?: number;
	labels?: string[] | null;
	dueDate?: number | null;
	links?: TaskLink[] | null;
	lastEditedBy?: string | null;
	activityLog?: TaskActivityLogEntry[];
	/**
	 * Schema version for task data structure.
	 * - undefined or 1: Original schema (no geometry blob)
	 * - 2: With embedded geometry blob support
	 */
	v?: number;
}

export interface DrawingTaskDetails {
	id: string;
	userId: string;
	createdAt?: number;
	notes: DrawingTaskNote[];
	title?: string | null;
	assignedTo?: string | null;
	responsibleOrganization?: string | null;
	/** Project Team answerable for this item; its members are Followers (ADR 0036). */
	responsibleTeamId?: string | null;
	priority?: number;
	labels?: string[] | null;
	dueDate?: number | null;
	lastEditedBy?: string | null;
	activityLog?: TaskActivityLogEntry[];
	/**
	 * Schema version for task detail data structure.
	 * Geometry-owned fields live only in PublicDrawingTaskSummary.
	 */
	v?: number;
}

export interface PublicDrawingTaskSummary {
	id: string;
	state: TaskState;
	page: number | null;
	objects: DrawingTaskObject[];
	timestamp: number;
	links?: TaskLink[] | null;
}

/**
 * Migrated object data with binary geometry blob.
 * Used for task objects that don't exist in the current drawing revision.
 */
export interface MigratedObjectData {
	h: string;
	page: number;
	layerId: number;
	layerName: string;
	bounds: TaskBounds;
	centroid: TaskCentroid;
	/**
	 * Binary geometry blob containing fills, strokes, and circles.
	 * Use decodeGeometryBlob() from geometry-encoding.ts to decode.
	 */
	geometryBlob: Uint8Array;
	metadata?: {
		kind?: string;
		originalLayerId?: number;
		sourceObjectHash?: string | null;
		[key: string]: unknown;
	};
}

export interface DrawingState {
	tasks: Array<DrawingTask>;
	migratedObjects: Array<MigratedObjectData>;
}

// ── Comment data structure ──

/**
 * Where an imported comment came from (PLA-517): a PDF annotation read from a
 * FileVersion. The comment belongs to the uploader; `author` is the PDF's.
 */
export interface CommentImportSource {
	kind: "pdfAnnotation";
	/** Stable identity of the annotation; dedupes imports across revisions. */
	key: string;
	/** FileVersion (project_file_instances id) it was read from. */
	fileVersionId: string;
	/** The annotation's author (`/T`) as written in the PDF. */
	author?: string;
	/** PDF annotation subtype, e.g. "FreeText". */
	subtype: string;
	/** `/Subj`: the markup's tool or category, e.g. a Bluebeam count tool. */
	subject?: string;
	/** `/IT`, e.g. "FreeTextTypewriter". */
	intent?: string;
	/** Unix ms, from the PDF. */
	createdAt?: number;
	modifiedAt?: number;
}

export interface DrawingComment {
	id: string;
	/** Which drawing this comment belongs to */
	drawingId: string;
	/** Denormalized for UI convenience */
	projectId: string;
	/** Separate field name from DrawingTask.state */
	commentState: CommentState;
	/** How this comment was created */
	commentType: CommentType;
	userId: string;
	/** null only for non-paged drawings; filtering treats null as "all pages" */
	page: number | null;
	/** Empty array for "note" type — enforced at creation */
	objects: DrawingTaskObject[];
	/** Unix ms — single source of truth for creation time & sorting */
	createdAt: number;
	/** Unix ms — updated on any mutation (state, title, notes, etc.) */
	updatedAt: number;
	/** userId of last editor */
	updatedBy?: string;
	notes: DrawingTaskNote[];
	subject?: string | null;
	assignedTo?: string | null;
	responsibleOrganization?: string | null;
	/** Project Team answerable for this item; its members are Followers (ADR 0036). */
	responsibleTeamId?: string | null;
	priority?: number;
	labels?: string[] | null;
	dueDate?: number | null;
	links?: TaskLink[] | null;
	activityLog?: TaskActivityLogEntry[];
	notifyUserIds?: string[] | null;
	/** For "note" type only: the world-space position where the pin was placed */
	pinPosition?: IPoint;
	/** Page frame `pinPosition` and `objects` are expressed in (see GeometryFrame). */
	geometryFrame?: GeometryFrame;
	/** Unix ms timestamp when archived, or null if active. Archived comments are hidden from all views by default. */
	archivedAt?: number | null;
	/** userId of the user who archived this comment */
	archivedBy?: string | null;
	/** Set when the comment was imported, not drawn in the app (PLA-517). */
	importSource?: CommentImportSource;
	/** Schema version — always set to COMMENT_SCHEMA_VERSION on creation */
	v: number;
}
